import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { ArrowRight } from "lucide-react";

type BalanceResponse =
  | { hasAccount: false }
  | { hasAccount: true; verified: false }
  | {
      hasAccount: true;
      verified: true;
      currency: string;
      availableMinor: number;
      pendingMinor: number;
      nextPayout: {
        amountMinor: number;
        arrivalDate: string;
        status: string;
      } | null;
    };

function formatMoney(minor: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency.toUpperCase(),
      maximumFractionDigits: 2,
    }).format(minor / 100);
  } catch {
    return `$${(minor / 100).toFixed(2)}`;
  }
}

function formatArrival(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "soon";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const arrival = new Date(d);
  arrival.setHours(0, 0, 0, 0);
  const days = Math.round((arrival.getTime() - today.getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "tomorrow";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

export function SellerBalanceTile() {
  // staleTime 30s to bypass global staleTime: Infinity, refetchOnWindowFocus
  // so returning to the dashboard from Stripe surfaces fresh numbers.
  const { data, isLoading } = useQuery<BalanceResponse>({
    queryKey: ["/api/seller/stripe-balance"],
    queryFn: async () => {
      const res = await fetch("/api/seller/stripe-balance", { credentials: "include" });
      if (!res.ok) throw new Error("Balance fetch failed");
      return res.json();
    },
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 pt-3">
        <div className="h-24 animate-pulse rounded-2xl bg-muted/60" />
      </div>
    );
  }
  if (!data || !data.hasAccount || !("verified" in data) || !data.verified) {
    return null;
  }

  const available = formatMoney(data.availableMinor, data.currency);
  const pending = formatMoney(data.pendingMinor, data.currency);

  return (
    <div className="container mx-auto px-4 pt-3">
      <Link
        href="/seller/payouts"
        className="block rounded-2xl border border-border bg-card p-4 shadow-sm transition-colors hover:bg-accent/40"
        data-testid="card-seller-balance"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Available for payout
            </p>
            <p className="mt-0.5 font-display text-3xl font-bold text-foreground">
              {available}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{pending}</span> in transit
              {data.nextPayout ? (
                <>
                  {" · arrives "}
                  <span className="font-medium text-foreground">
                    {formatArrival(data.nextPayout.arrivalDate)}
                  </span>
                </>
              ) : null}
            </p>
          </div>
          <ArrowRight className="mt-1 h-5 w-5 flex-shrink-0 text-muted-foreground" />
        </div>
        <p className="mt-3 text-xs font-semibold text-primary">View payouts &amp; statements</p>
      </Link>
    </div>
  );
}
